/**
 * LinkedIn campaign configuration rules: which type each format needs, which objectives and bidding
 * each format accepts, where LAN/Audience Expansion can be on, and each Ad Segment type's facet.
 * Probed live on 2026-10-01 against ad account 503024584 — LinkedIn's docs disagree with the API
 * here, so don't "fix" these from the docs. See SCHEMA_ARCHITECTURE.md's "Campaign configuration rules".
 */

import {
    LinkedInCampaignFormat,
    LinkedInCampaignObjective,
    LinkedInCampaignTargetType,
    LinkedInCampaignType,
    LinkedInCostType,
} from './campaign'
import { LinkedInCampaignGroupBidStrategy } from './campaignGroup'

type Objective = LinkedInCampaignObjective
type TargetType = LinkedInCampaignTargetType

const O = LinkedInCampaignObjective
const T = LinkedInCampaignTargetType
const { CPC, CPM, CPV } = LinkedInCostType

export interface CampaignConfiguration {
    targets: Readonly<Partial<Record<string, readonly string[]>>>
    lan: boolean
    audienceExpansion: boolean
}

type Bidding = Partial<Record<TargetType, LinkedInCostType[]>>

interface FormatRules {
    type: LinkedInCampaignType
    audienceExpansion: boolean
    lan: boolean
    noLanFor?: Objective[]
    objectives: Partial<Record<Objective, Bidding>>
}

const MANUAL_CPM: Bidding = { [T.NONE]: [CPM] }
const MANUAL: Bidding = { [T.NONE]: [CPC, CPM] }
const MESSAGING: Bidding = { [T.NONE]: [CPM], [T.MAX_IMPRESSION]: [CPM] }

// Sponsored Content bidding per objective — the same in every feed format that takes the objective.
const FEED_BIDDING: Record<Objective, Bidding> = {
    [O.BRAND_AWARENESS]: {
        [T.NONE]: [CPM],
        [T.MAX_IMPRESSION]: [CPM],
        [T.MAX_REACH]: [CPM],
        [T.TARGET_COST_PER_IMPRESSION]: [CPM],
        [T.CAP_COST_AND_MAXIMIZE_IMPRESSIONS]: [CPM],
    },
    [O.ENGAGEMENT]: {
        [T.NONE]: [CPC, CPM],
        [T.ENHANCED_CONVERSION]: [CPC],
        [T.MAX_CLICK]: [CPM],
        [T.TARGET_COST_PER_CLICK]: [CPC],
        [T.CAP_COST_AND_MAXIMIZE_CLICKS]: [CPM],
    },
    [O.JOB_APPLICANT]: { [T.NONE]: [CPC, CPM], [T.MAX_CLICK]: [CPM] },
    [O.LEAD_GENERATION]: {
        [T.NONE]: [CPC, CPM],
        [T.ENHANCED_CONVERSION]: [CPC],
        [T.MAX_LEAD]: [CPM],
        [T.MAX_QUALIFIED_LEAD]: [CPM],
        [T.CAP_COST_AND_MAXIMIZE_LEADS]: [CPM],
    },
    [O.TALENT_LEAD]: {
        [T.NONE]: [CPC, CPM],
        [T.ENHANCED_CONVERSION]: [CPC],
        [T.MAX_CLICK]: [CPM],
        [T.MAX_LEAD]: [CPM],
    },
    [O.VIDEO_VIEW]: {
        [T.NONE]: [CPM, CPV],
        [T.MAX_VIDEO_VIEW]: [CPM],
        [T.TARGET_COST_PER_VIDEO_VIEW]: [CPV],
        [T.CAP_COST_AND_MAXIMIZE_VIDEO_VIEWS]: [CPM],
    },
    [O.WEBSITE_CONVERSION]: {
        [T.NONE]: [CPC, CPM],
        [T.ENHANCED_CONVERSION]: [CPC],
        [T.MAX_CONVERSION]: [CPM],
        [T.MAX_QUALIFIED_LEAD]: [CPM],
    },
    [O.WEBSITE_VISIT]: {
        [T.NONE]: [CPC, CPM],
        [T.ENHANCED_CONVERSION]: [CPC],
        [T.MAX_CLICK]: [CPM],
        [T.TARGET_COST_PER_CLICK]: [CPC],
        [T.CAP_COST_AND_MAXIMIZE_CLICKS]: [CPM],
    },
    [O.WEBSITE_TRAFFIC]: {
        [T.NONE]: [CPC, CPM],
        [T.ENHANCED_CONVERSION]: [CPC],
        [T.MAX_CLICK]: [CPM],
        [T.MAX_CONVERSION]: [CPM],
        [T.MAX_IMPRESSION]: [CPM],
    },
    [O.CREATIVE_ENGAGEMENT]: {
        [T.NONE]: [CPC, CPM],
        [T.ENHANCED_CONVERSION]: [CPC],
        [T.MAX_CLICK]: [CPM],
        [T.MAX_CONVERSION]: [CPM],
        [T.MAX_IMPRESSION]: [CPM],
    },
}

function feed(...objectives: Objective[]): Partial<Record<Objective, Bidding>> {
    return Object.fromEntries(objectives.map(objective => [objective, FEED_BIDDING[objective]]))
}

const SPONSORED_CONTENT = { type: LinkedInCampaignType.SPONSORED_UPDATES, audienceExpansion: true }
const MESSAGING_FORMAT = {
    type: LinkedInCampaignType.SPONSORED_INMAILS,
    audienceExpansion: true,
    lan: false,
}
const DYNAMIC_FORMAT = { type: LinkedInCampaignType.DYNAMIC, audienceExpansion: false, lan: false }

const FORMAT_RULES = {
    [LinkedInCampaignFormat.STANDARD_UPDATE]: {
        ...SPONSORED_CONTENT,
        lan: true,
        noLanFor: [O.LEAD_GENERATION],
        objectives: feed(
            O.BRAND_AWARENESS,
            O.ENGAGEMENT,
            O.JOB_APPLICANT,
            O.LEAD_GENERATION,
            O.TALENT_LEAD,
            O.WEBSITE_CONVERSION,
            O.WEBSITE_VISIT,
            O.WEBSITE_TRAFFIC,
            O.CREATIVE_ENGAGEMENT
        ),
    },
    [LinkedInCampaignFormat.CAROUSEL]: {
        ...SPONSORED_CONTENT,
        lan: true,
        noLanFor: [O.LEAD_GENERATION, O.TALENT_LEAD],
        objectives: feed(
            O.BRAND_AWARENESS,
            O.ENGAGEMENT,
            O.LEAD_GENERATION,
            O.TALENT_LEAD,
            O.WEBSITE_CONVERSION,
            O.WEBSITE_VISIT,
            O.WEBSITE_TRAFFIC,
            O.CREATIVE_ENGAGEMENT
        ),
    },
    [LinkedInCampaignFormat.SINGLE_VIDEO]: {
        ...SPONSORED_CONTENT,
        lan: true,
        noLanFor: [O.LEAD_GENERATION],
        objectives: feed(
            O.BRAND_AWARENESS,
            O.ENGAGEMENT,
            O.LEAD_GENERATION,
            O.TALENT_LEAD,
            O.VIDEO_VIEW,
            O.WEBSITE_CONVERSION,
            O.WEBSITE_VISIT,
            O.WEBSITE_TRAFFIC,
            O.CREATIVE_ENGAGEMENT
        ),
    },
    [LinkedInCampaignFormat.SPONSORED_UPDATE_EVENT]: {
        ...SPONSORED_CONTENT,
        lan: false,
        objectives: feed(O.BRAND_AWARENESS, O.ENGAGEMENT, O.LEAD_GENERATION, O.WEBSITE_VISIT),
    },
    [LinkedInCampaignFormat.SPONSORED_UPDATE_NATIVE_DOCUMENT]: {
        ...SPONSORED_CONTENT,
        lan: true,
        noLanFor: [O.LEAD_GENERATION],
        objectives: feed(
            O.BRAND_AWARENESS,
            O.ENGAGEMENT,
            O.LEAD_GENERATION,
            O.WEBSITE_CONVERSION,
            O.WEBSITE_VISIT
        ),
    },
    [LinkedInCampaignFormat.SPONSORED_UPDATE_JOB_POSTING]: {
        ...SPONSORED_CONTENT,
        lan: false,
        objectives: feed(O.JOB_APPLICANT),
    },
    [LinkedInCampaignFormat.SPONSORED_INMAIL]: {
        ...MESSAGING_FORMAT,
        objectives: {
            [O.LEAD_GENERATION]: MESSAGING,
            [O.WEBSITE_CONVERSION]: MESSAGING,
            [O.WEBSITE_VISIT]: MESSAGING,
            [O.WEBSITE_TRAFFIC]: MANUAL_CPM,
        },
    },
    [LinkedInCampaignFormat.SPONSORED_MESSAGE]: {
        ...MESSAGING_FORMAT,
        objectives: {
            [O.BRAND_AWARENESS]: MESSAGING,
            [O.ENGAGEMENT]: MESSAGING,
            [O.LEAD_GENERATION]: MESSAGING,
            [O.WEBSITE_CONVERSION]: MESSAGING,
            [O.WEBSITE_VISIT]: MESSAGING,
            [O.WEBSITE_TRAFFIC]: MANUAL_CPM,
        },
    },
    [LinkedInCampaignFormat.TEXT_AD]: {
        type: LinkedInCampaignType.TEXT_AD,
        audienceExpansion: true,
        lan: false,
        objectives: {
            [O.BRAND_AWARENESS]: MANUAL_CPM,
            [O.WEBSITE_CONVERSION]: MANUAL,
            [O.WEBSITE_VISIT]: MANUAL,
            [O.WEBSITE_TRAFFIC]: MANUAL,
        },
    },
    [LinkedInCampaignFormat.SPOTLIGHT]: {
        ...DYNAMIC_FORMAT,
        objectives: {
            [O.BRAND_AWARENESS]: MANUAL_CPM,
            [O.JOB_APPLICANT]: MANUAL,
            [O.TALENT_LEAD]: MANUAL_CPM,
            [O.WEBSITE_CONVERSION]: MANUAL,
            [O.WEBSITE_VISIT]: MANUAL,
            [O.WEBSITE_TRAFFIC]: MANUAL,
        },
    },
    [LinkedInCampaignFormat.FOLLOW_COMPANY]: {
        ...DYNAMIC_FORMAT,
        objectives: {
            [O.BRAND_AWARENESS]: MANUAL_CPM,
            [O.ENGAGEMENT]: MANUAL,
            [O.WEBSITE_TRAFFIC]: MANUAL,
        },
    },
    [LinkedInCampaignFormat.JOBS]: {
        ...DYNAMIC_FORMAT,
        objectives: { [O.JOB_APPLICANT]: MANUAL_CPM, [O.WEBSITE_TRAFFIC]: MANUAL_CPM },
    },
} satisfies Partial<Record<LinkedInCampaignFormat, FormatRules>>

// A SPONSORED_UPDATES campaign created without a format; its first creative sets one.
const NO_FORMAT_RULES: FormatRules = {
    ...SPONSORED_CONTENT,
    lan: true,
    noLanFor: [O.LEAD_GENERATION],
    objectives: {
        ...FEED_BIDDING,
        [O.ENGAGEMENT]: { ...FEED_BIDDING[O.ENGAGEMENT], [T.MAX_REACH]: [CPM] },
    },
}

const SEGMENT_TYPE_FACETS: Readonly<Record<string, string>> = {
    BULK: 'audienceMatchingSegments',
    MARKET_AUTOMATION: 'audienceMatchingSegments',
    BUSINESS_OBJECTIVE_BASED_AUDIENCE: 'audienceMatchingSegments',
    RETARGETING: 'dynamicSegments',
    LOOKALIKE: 'dynamicSegments',
}

type CampaignFormatValue = keyof typeof FORMAT_RULES

function configurationsOf(rules: FormatRules): Partial<Record<string, CampaignConfiguration>> {
    return Object.fromEntries(
        Object.entries(rules.objectives).map(([objective, targets]) => [
            objective,
            {
                targets,
                lan: rules.lan && !(rules.noLanFor ?? []).includes(objective as Objective),
                audienceExpansion: rules.audienceExpansion,
            },
        ])
    )
}

// Rows keyed by format, plus UNSET_FORMAT for a SPONSORED_UPDATES campaign with none.
const UNSET_FORMAT = 'UNSET'

const CONFIGURATIONS: Readonly<Record<string, Partial<Record<string, CampaignConfiguration>>>> = {
    [UNSET_FORMAT]: configurationsOf(NO_FORMAT_RULES),
    ...Object.fromEntries(
        Object.entries(FORMAT_RULES).map(([format, rules]) => [format, configurationsOf(rules)])
    ),
}

const FORMAT_TYPES: Readonly<Record<string, string>> = Object.fromEntries(
    Object.entries(FORMAT_RULES).map(([format, rules]) => [format, rules.type])
)

/** Every value LinkedIn accepts as campaign.format — narrower than LinkedInCampaignFormat, which also carries read-only values. */
export const CAMPAIGN_FORMAT_VALUES = Object.fromEntries(
    Object.keys(FORMAT_RULES).map(format => [format, format])
) as { [F in CampaignFormatValue]: F }

/** campaign.format values that need a Conversion associated before the campaign can serve, per LinkedIn's docs. */
const CONVERSION_TRACKING_REQUIRED: Partial<Record<Objective, LinkedInCampaignFormat[]>> = {
    [LinkedInCampaignObjective.BRAND_AWARENESS]: [LinkedInCampaignFormat.FOLLOW_COMPANY],
    [LinkedInCampaignObjective.WEBSITE_CONVERSION]: [
        LinkedInCampaignFormat.SINGLE_VIDEO,
        LinkedInCampaignFormat.CAROUSEL,
        LinkedInCampaignFormat.TEXT_AD,
        LinkedInCampaignFormat.SPOTLIGHT,
        LinkedInCampaignFormat.SPONSORED_INMAIL,
        LinkedInCampaignFormat.SPONSORED_MESSAGE,
    ],
}

// LinkedIn named every other format (and none) when rejecting ACCELERATED in QA's mutation_logs, 2026-10-05.
export const ACCELERATED_PACING_FORMATS: string[] = [LinkedInCampaignFormat.SPONSORED_UPDATE_EVENT]

export const ACCELERATED_PACING_MAX_HOURS = 24

/** Manual-bidding target types — disallowed for Connected Television Only campaigns. */
export const MANUAL_BIDDING_TARGET_TYPES: TargetType[] = [
    LinkedInCampaignTargetType.NONE,
    LinkedInCampaignTargetType.ENHANCED_CONVERSION,
]

/**
 * (objectiveType, campaign group budgetOptimization.bidStrategy) -> the one
 * optimizationTargetType LinkedIn accepts on a campaign in that group, or null
 * when the group itself is rejected with that bid strategy. Not documented
 * anywhere; probed live on 2026-10-01 against every target type each objective
 * allows (CONDITIONAL_INVALID_VALUE for every other one). Combinations that
 * couldn't be confirmed are left out rather than guessed.
 */
const GROUP_BID_STRATEGY_TARGET_TYPE: Partial<
    Record<Objective, Partial<Record<LinkedInCampaignGroupBidStrategy, TargetType | null>>>
> = {
    [LinkedInCampaignObjective.BRAND_AWARENESS]: {
        [LinkedInCampaignGroupBidStrategy.MAXIMUM_DELIVERY]: LinkedInCampaignTargetType.MAX_REACH,
        [LinkedInCampaignGroupBidStrategy.MANUAL]: null,
        [LinkedInCampaignGroupBidStrategy.COST_CAP]: null,
    },
    [LinkedInCampaignObjective.WEBSITE_VISIT]: {
        [LinkedInCampaignGroupBidStrategy.MAXIMUM_DELIVERY]: LinkedInCampaignTargetType.MAX_CLICK,
        [LinkedInCampaignGroupBidStrategy.MANUAL]: LinkedInCampaignTargetType.ENHANCED_CONVERSION,
        [LinkedInCampaignGroupBidStrategy.COST_CAP]:
            LinkedInCampaignTargetType.CAP_COST_AND_MAXIMIZE_CLICKS,
    },
    [LinkedInCampaignObjective.ENGAGEMENT]: {
        [LinkedInCampaignGroupBidStrategy.MAXIMUM_DELIVERY]: LinkedInCampaignTargetType.MAX_CLICK,
        [LinkedInCampaignGroupBidStrategy.MANUAL]: LinkedInCampaignTargetType.ENHANCED_CONVERSION,
        [LinkedInCampaignGroupBidStrategy.COST_CAP]:
            LinkedInCampaignTargetType.CAP_COST_AND_MAXIMIZE_CLICKS,
    },
    [LinkedInCampaignObjective.LEAD_GENERATION]: {
        [LinkedInCampaignGroupBidStrategy.MAXIMUM_DELIVERY]: LinkedInCampaignTargetType.MAX_LEAD,
        [LinkedInCampaignGroupBidStrategy.MANUAL]: LinkedInCampaignTargetType.ENHANCED_CONVERSION,
        [LinkedInCampaignGroupBidStrategy.COST_CAP]:
            LinkedInCampaignTargetType.CAP_COST_AND_MAXIMIZE_LEADS,
    },
    [LinkedInCampaignObjective.WEBSITE_CONVERSION]: {
        [LinkedInCampaignGroupBidStrategy.MAXIMUM_DELIVERY]:
            LinkedInCampaignTargetType.MAX_CONVERSION,
        [LinkedInCampaignGroupBidStrategy.MANUAL]: LinkedInCampaignTargetType.ENHANCED_CONVERSION,
        [LinkedInCampaignGroupBidStrategy.COST_CAP]: null,
    },
    [LinkedInCampaignObjective.VIDEO_VIEW]: {
        [LinkedInCampaignGroupBidStrategy.MANUAL]: LinkedInCampaignTargetType.NONE,
        [LinkedInCampaignGroupBidStrategy.COST_CAP]:
            LinkedInCampaignTargetType.CAP_COST_AND_MAXIMIZE_VIDEO_VIEWS,
    },
    [LinkedInCampaignObjective.JOB_APPLICANT]: {
        [LinkedInCampaignGroupBidStrategy.MAXIMUM_DELIVERY]: LinkedInCampaignTargetType.MAX_CLICK,
        [LinkedInCampaignGroupBidStrategy.MANUAL]: LinkedInCampaignTargetType.NONE,
        [LinkedInCampaignGroupBidStrategy.COST_CAP]: null,
    },
}

export function getCampaignFormatType(format: string): string | undefined {
    return FORMAT_TYPES[format]
}

/**
 * What LinkedIn accepts for this format and objective, or undefined when it rejects the pair.
 * `format` undefined means a campaign of `type` created without one — only SPONSORED_UPDATES has a
 * probed row for that, so any other type with no format returns undefined too.
 */
export function getCampaignConfiguration(
    type: string | undefined,
    format: string | undefined,
    objective: string
): CampaignConfiguration | undefined {
    const row =
        format ?? (type === LinkedInCampaignType.SPONSORED_UPDATES ? UNSET_FORMAT : undefined)

    return row === undefined ? undefined : CONFIGURATIONS[row]?.[objective]
}

function candidateRows(type: string | undefined, format: string | undefined): string[] {
    if (format !== undefined) return format in CONFIGURATIONS ? [format] : []

    if (type === LinkedInCampaignType.SPONSORED_UPDATES) return [UNSET_FORMAT]

    return Object.keys(CAMPAIGN_FORMAT_VALUES).filter(
        row => type === undefined || FORMAT_TYPES[row] === type
    )
}

/**
 * What LinkedIn accepts for `objective` in each format the campaign could have — just its own when
 * `format` is known, every format of its type (or of any type) otherwise. Undefined when there is
 * no probed format to judge by, e.g. a placeholder format like $UNKNOWN on a live campaign.
 */
export function getCampaignConfigurations(
    type: string | undefined,
    format: string | undefined,
    objective: string
): CampaignConfiguration[] | undefined {
    const rows = candidateRows(type, format)

    if (rows.length === 0) return undefined

    return rows
        .map(row => CONFIGURATIONS[row]?.[objective])
        .filter((configuration): configuration is CampaignConfiguration => !!configuration)
}

/** campaign.format values LinkedIn accepts with `objective`, optionally only those of one campaign type. */
export function getObjectiveFormats(objective: string, type?: string): CampaignFormatValue[] {
    return (Object.keys(CAMPAIGN_FORMAT_VALUES) as CampaignFormatValue[]).filter(
        format =>
            CONFIGURATIONS[format]?.[objective] !== undefined &&
            (type === undefined || FORMAT_TYPES[format] === type)
    )
}

function groupBy(entries: [string, string][]): [string, string[]][] {
    const groups = new Map<string, string[]>()

    for (const [key, group] of entries) groups.set(group, [...(groups.get(group) ?? []), key])

    return [...groups]
}

/** e.g. "BULK and MARKET_AUTOMATION under audienceMatchingSegments; RETARGETING and LOOKALIKE under dynamicSegments". */
export function describeSegmentTypeFacets(): string {
    return groupBy(Object.entries(SEGMENT_TYPE_FACETS))
        .map(([facet, types]) => `${types.join(' and ')} under ${facet}`)
        .join('; ')
}

/** e.g. "SPONSORED_INMAILS for SPONSORED_INMAIL/SPONSORED_MESSAGE; ...". */
export function describeCampaignFormatTypes(): string {
    return groupBy(
        Object.keys(CAMPAIGN_FORMAT_VALUES).map(format => [format, FORMAT_TYPES[format]])
    )
        .map(([type, formats]) => `${type} for ${formats.join('/')}`)
        .join('; ')
}

/** The targeting facet an ad segment of this type belongs under. */
export function getSegmentTypeFacet(segmentType: string): string | undefined {
    return SEGMENT_TYPE_FACETS[segmentType]
}

export function getGroupBidStrategyTargetType(
    objective: Objective,
    bidStrategy: LinkedInCampaignGroupBidStrategy
): TargetType | null | undefined {
    return GROUP_BID_STRATEGY_TARGET_TYPE[objective]?.[bidStrategy]
}

/** GROUP_BID_STRATEGY_TARGET_TYPE as one line per objective, for linkedin_describe_mutation. */
export function buildGroupBidStrategyRules(): string {
    return Object.entries(GROUP_BID_STRATEGY_TARGET_TYPE)
        .map(([objective, byStrategy]) => {
            const rules = Object.entries(byStrategy ?? {}).map(([strategy, target]) =>
                target === null ? `${strategy} not allowed` : `${strategy} -> ${target}`
            )

            return `- ${objective}: ${rules.join('; ')}`
        })
        .join('\n')
}

export function isManualBiddingTargetType(targetType: TargetType): boolean {
    return MANUAL_BIDDING_TARGET_TYPES.includes(targetType)
}

function describeTargets(configuration: CampaignConfiguration): string {
    return Object.entries(configuration.targets)
        .map(([target, costs]) => `${target} (${(costs ?? []).join('/')})`)
        .join(', ')
}

/** The generated rules as markdown tables, for `linkedin_describe_mutation`. */
export function buildObjectiveCompatibilityMatrix(): string {
    const lines: string[] = []

    lines.push('#### campaign.format -> required campaign type')
    lines.push('| format | type |', '|---|---|')
    for (const format of Object.keys(CAMPAIGN_FORMAT_VALUES)) {
        lines.push(`| ${format} | ${FORMAT_TYPES[format]} |`)
    }
    lines.push(
        '',
        'A SPONSORED_UPDATES campaign may also omit format (its first creative sets it) — that is the ' +
            '"(none)" row below. Every other type needs its format set.'
    )

    lines.push(
        '',
        '#### (format, objectiveType) -> allowed optimizationTargetType (allowed costType), LinkedIn Audience Network (offsiteDeliveryEnabled), Audience Expansion',
        'A (format, objectiveType) pair missing here is rejected by LinkedIn. Manual bidding is NONE, ' +
            'the default when optimizationTargetType is omitted.'
    )
    lines.push(
        '| format | objectiveType | optimizationTargetType (costType) | LAN | Audience Expansion |',
        '|---|---|---|---|---|'
    )
    for (const [format, byObjective] of Object.entries(CONFIGURATIONS)) {
        for (const [objective, configuration] of Object.entries(byObjective)) {
            if (!configuration) continue

            lines.push(
                `| ${format === UNSET_FORMAT ? '(none)' : format} | ${objective} | ` +
                    `${describeTargets(configuration)} | ` +
                    `${configuration.lan ? 'allowed' : 'not allowed'} | ` +
                    `${configuration.audienceExpansion ? 'allowed' : 'not allowed'} |`
            )
        }
    }

    lines.push(
        '',
        'Needs a Conversion associated (campaignConversionAssociation.create) before it can serve: ' +
            Object.entries(CONVERSION_TRACKING_REQUIRED)
                .map(([objective, formats]) => `${objective} with ${(formats ?? []).join(', ')}`)
                .join('; ') +
            '.',
        `Manual-bidding optimizationTargetType values (disallowed for connectedTelevisionOnly campaigns): ${MANUAL_BIDDING_TARGET_TYPES.join(', ')}.`
    )

    return lines.join('\n')
}
